import { RECOVERY_SUPPORT_EMAIL } from '../model/recoverySupport';
import { VITE_BFF_SERVICE_URL } from '@/shared/config/envConfig';

interface PasswordPolicyFields {
  allow_spaces: true;
  length_unit: 'utf16';
  require_composition: false;
  require_non_blank: true;
}

export type PasswordPolicy = PasswordPolicyFields & (
  { max_length: 128; min_length: 15; version: '1' }
  | { max_length: 128; min_length: 6; version: '2' }
  | { max_length: 64; min_length: 6; version: '3' }
);

export type RecoveryStatus = {
  status: 'completed' | 'processing' | 'support_required';
} | {
  can_retry: boolean;
  reason: 'invalid_or_expired_token' | 'password_policy_violation' | 'temporarily_unavailable';
  status: 'rejected';
};

const errorMessages: Record<string, string> = {
  invalid_email: 'Введите корректную электронную почту.',
  invalid_or_expired_token: `Ссылка больше не действует. Откройте письмо ещё раз или обратитесь в поддержку White Line: ${RECOVERY_SUPPORT_EMAIL}.`,
  invalid_request: 'Не удалось обработать запрос. Проверьте введённые данные.',
  password_policy_violation: 'Пароль не соответствует требованиям. Выберите другой пароль.',
  recovery_in_progress: 'Восстановление уже выполняется. Проверяем его результат.',
  recovery_unavailable: 'Восстановление временно недоступно. Попробуйте позже.',
  temporarily_unavailable: 'Сервис временно недоступен. Попробуйте позже.'
};

export class RecoveryRequestError extends Error {
  readonly code: string;
  readonly retryAfterSeconds: number;
  readonly status: number;

  constructor(status: number, code: string, retryAfterSeconds = 0) {
    super(status === 429 ? 'Слишком много запросов. Подождите перед повтором.'
      : status === 413 ? 'Запрос слишком большой.'
      : errorMessages[code] ?? 'Не удалось связаться с сервисом. Попробуйте ещё раз.');
    this.name = 'RecoveryRequestError';
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
    this.status = status;
  }
}

const record = (value: unknown): Record<string, unknown> => {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
};

const request = async (path: string, payload?: object, signal?: AbortSignal): Promise<Record<string, unknown>> => {
  const controller = new AbortController();
  const abort = (): void => controller.abort();
  if (signal?.aborted) abort();
  else signal?.addEventListener('abort', abort, { once: true });
  const timeoutId = window.setTimeout(() => controller.abort(), 30_000);
  try {
    const response = await fetch(`${VITE_BFF_SERVICE_URL}${path}`, {
      body: payload ? JSON.stringify(payload) : undefined,
      cache: 'no-store',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      method: payload ? 'POST' : 'GET',
      redirect: 'error',
      referrerPolicy: 'no-referrer',
      signal: controller.signal
    });
    const data = record(await response.json().catch(() => null));
    if (!response.ok) {
      const rawCode = data.error_code;
      const code = typeof rawCode === 'string' && (Object.prototype.hasOwnProperty.call(errorMessages, rawCode) || rawCode === 'operation_not_found')
        ? rawCode : 'request_failed';
      const bodyDelay = record(data.details).retry_after_seconds;
      const retryHeader = response.headers.get('Retry-After');
      const headerDelay = retryHeader && /^\d+$/.test(retryHeader)
        ? Number(retryHeader) : retryHeader ? Math.ceil((Date.parse(retryHeader) - Date.now()) / 1000) : 0;
      const delay = typeof bodyDelay === 'number' ? bodyDelay : headerDelay;
      throw new RecoveryRequestError(response.status, code, Number.isFinite(delay) && delay > 0 ? Math.ceil(delay) : 0);
    }
    return data;
  } catch (error) {
    throw error instanceof RecoveryRequestError ? error : new RecoveryRequestError(0, 'request_failed');
  } finally {
    window.clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abort);
  }
};

export const getPasswordPolicy = async (): Promise<PasswordPolicy> => {
  const data = await request('/api/auth/password-policy');
  const isSupportedPolicy = data.version === '1' && data.min_length === 15 && data.max_length === 128
    || data.version === '2' && data.min_length === 6 && data.max_length === 128
    || data.version === '3' && data.min_length === 6 && data.max_length === 64;
  if (!isSupportedPolicy
    || data.length_unit !== 'utf16' || data.allow_spaces !== true || data.require_composition !== false
    || data.require_non_blank !== true) {
    throw new RecoveryRequestError(0, 'temporarily_unavailable');
  }
  const fields: PasswordPolicyFields = {
    allow_spaces: true,
    length_unit: 'utf16',
    require_composition: false,
    require_non_blank: true
  };
  if (data.version === '3') return { ...fields, max_length: 64, min_length: 6, version: '3' };
  return data.version === '2'
    ? { ...fields, max_length: 128, min_length: 6, version: '2' }
    : { ...fields, max_length: 128, min_length: 15, version: '1' };
};

export interface RequestPasswordResetRequest {
  email: string;
}

export interface RequestPasswordResetResponse {
  retry_after_seconds: number;
  status: 'accepted';
}

export const requestPasswordReset = async (
  payload: RequestPasswordResetRequest
): Promise<RequestPasswordResetResponse> => {
  const data = await request('/api/auth/password-reset/request', payload);
  if (data.status !== 'accepted' || data.retry_after_seconds !== 60) {
    throw new RecoveryRequestError(0, 'request_failed');
  }
  return { retry_after_seconds: 60, status: 'accepted' };
};

export interface InspectPasswordResetRequest {
  token: string;
}

export type InspectPasswordResetResponse = {
  expires_at: string;
  status: 'active';
} | {
  operation_id: string;
  status: 'operation';
};

const isUuid = (value: unknown): value is string => {
  return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
};

export const inspectPasswordReset = async (
  payload: InspectPasswordResetRequest,
  signal?: AbortSignal
): Promise<InspectPasswordResetResponse> => {
  const data = await request('/api/auth/password-reset/inspect', payload, signal);
  if (data.status === 'active' && typeof data.expires_at === 'string' && Number.isFinite(Date.parse(data.expires_at))) {
    return { expires_at: data.expires_at, status: 'active' };
  }
  if (data.status === 'operation' && isUuid(data.operation_id)) {
    return { operation_id: data.operation_id, status: 'operation' };
  }
  throw new RecoveryRequestError(0, 'request_failed');
};

const parseStatus = (data: Record<string, unknown>): RecoveryStatus => {
  if (data.status === 'completed' || data.status === 'processing' || data.status === 'support_required') {
    return { status: data.status };
  }
  if (data.status === 'rejected' && typeof data.can_retry === 'boolean'
    && (data.reason === 'invalid_or_expired_token' || data.reason === 'password_policy_violation'
      || data.reason === 'temporarily_unavailable')) {
    return { can_retry: data.can_retry, reason: data.reason, status: 'rejected' };
  }
  throw new RecoveryRequestError(0, 'request_failed');
};

export interface CompletePasswordResetRequest {
  new_password: string;
  operation_id: string;
  token: string;
}

export const completePasswordReset = async (
  payload: CompletePasswordResetRequest,
  signal?: AbortSignal
): Promise<RecoveryStatus> => {
  return parseStatus(await request('/api/auth/password-reset/complete', payload, signal));
};

export interface GetPasswordResetStatusRequest {
  operation_id: string;
  token: string;
}

export const getPasswordResetStatus = async (
  payload: GetPasswordResetStatusRequest,
  signal?: AbortSignal
): Promise<RecoveryStatus> => {
  return parseStatus(await request('/api/auth/password-reset/status', payload, signal));
};